import React, { useState, useEffect } from "react";
import { Spinner, HouseIcon } from "../components";
import { db } from "../utils/firebase";
import {
  onSnapshot,
  collection,
  updateDoc,
  doc,
  getDoc,
  setDoc,
  deleteDoc,
  query,
  where,
  getDocs,
  writeBatch,
} from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";
import { DoorOpen, HeartPulse, Info, Search, Home, X } from "lucide-react";
import { useAuth } from "../hooks/AuthenticationProvider";

const Residents = () => {
  const { user, organizationId, organizationData, checkPermission, isLoading } =
    useAuth();
  const navigate = useNavigate();
  const [residents, setResidents] = useState([]);
  const [dischargeModalOpen, setDischargeModalOpen] = useState(false);
  const [currentResidentId, setCurrentResidentId] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [houseFilter, setHouseFilter] = useState("All");
  const [searchInput, setSearchInput] = useState("");
  const [poReminderOpen, setPoReminderOpen] = useState(false);
  const [currentPOInfo, setCurrentPOInfo] = useState({
    poFirstName: "",
    poLastName: "",
  });

  // Permission checks
  const canDischargeResidents = checkPermission("dischargeResidents");
  const canViewProfile = checkPermission("viewProfile");

  const getHouseColors = (house) => {
    const houseData = organizationData?.housesData?.[house];
    if (!houseData) return {};

    const color = houseData.color;
    return {
      style: {
        background: `linear-gradient(to bottom, ${color}33, rgba(15, 23, 42, 0.5))`,
        borderColor: `${color}4D`,
        transition: "all 0.5s",
      },
      className: `hover:border-opacity-40`,
    };
  };

  useEffect(() => {
    if (!organizationId) return;

    const moveDischargedResidentsToAlumni = async () => {
      try {
        // Reference to residents collection
        const residentsRef = collection(
          db,
          `organizations/${organizationId}/residents`
        );

        // Get all residents where currentResident is false
        const dischargedResidentsQuery = query(
          residentsRef,
          where("currentResident", "==", false)
        );
        const dischargedSnapshot = await getDocs(dischargedResidentsQuery);

        // If there are no discharged residents, exit early
        if (dischargedSnapshot.empty) {
          console.log("No discharged residents found to move to alumni");
          return;
        }

        console.log(
          `Found ${dischargedSnapshot.size} discharged residents to move to alumni`
        );

        // Process each discharged resident
        const batch = writeBatch(db);

        for (const docSnapshot of dischargedSnapshot.docs) {
          const residentId = docSnapshot.id;
          const residentData = docSnapshot.data();

          // Create a reference to where this resident will be stored in alumni collection
          const alumniRef = doc(
            db,
            `organizations/${organizationId}/alumni`,
            residentId
          );

          // Add additional metadata for tracking
          const alumniData = {
            ...residentData,
            movedToAlumniAt: new Date().toISOString(),
            migratedAutomatically: true, // Flag to indicate this was moved by the automatic process
          };

          // Add to alumni collection
          batch.set(alumniRef, alumniData);

          // Delete from residents collection (optional - you could keep them with the flag)
          batch.delete(doc(residentsRef, residentId));
        }

        // Commit all the batch operations
        await batch.commit();
        console.log(
          "Successfully moved discharged residents to alumni collection"
        );
      } catch (error) {
        console.error("Error moving discharged residents to alumni:", error);
      }
    };

    // Run the cleanup process once when the component mounts
    moveDischargedResidentsToAlumni();
  }, [organizationId]);

  const openDischargeModal = async (id) => {
    try {
      const residentRef = doc(
        db,
        `organizations/${organizationId}/residents`,
        id
      );
      const residentSnapshot = await getDoc(residentRef);

      if (!residentSnapshot.exists()) {
        console.error(`Resident document with ID ${id} does not exist.`);
        return;
      }

      const residentData = residentSnapshot.data();

      // Check if resident has PO information
      if (residentData?.poFirstName?.trim()) {
        setCurrentPOInfo({
          poFirstName: residentData.poFirstName,
          poLastName: residentData.poLastName || "",
        });
        setPoReminderOpen(true); // Open the PO reminder modal
      } else {
        setDischargeModalOpen(true); // Open discharge modal directly
      }

      setCurrentResidentId(id); // Set current resident regardless of PO status
    } catch (err) {
      console.error("Error fetching resident data:", err);
    }
  };

  const closePoReminder = () => {
    setPoReminderOpen(false); // Close PO Reminder modal
    setDischargeModalOpen(true); // Open Discharge Modal
  };

  const cancelPoReminder = () => {
    setPoReminderOpen(false); // Close PO Reminder modal
    setCurrentResidentId(null); // Clear the current resident
    setCurrentPOInfo({ poFirstName: "", poLastName: "" }); // Reset PO info
  };

  useEffect(() => {
    if (!organizationId) return;

    const residentsRef = collection(
      db,
      `organizations/${organizationId}/residents`
    );
    const unsubscribe = onSnapshot(residentsRef, (snapshot) => {
      const updatedResidents = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setResidents(updatedResidents); // Update local state
    });

    // Cleanup listener on unmount
    return () => unsubscribe();
  }, [organizationId]);

  const calculateDuration = (startDate, endDate = new Date()) => {
    const start = new Date(startDate);
    const end = new Date(endDate);

    // Normalize both dates to UTC midnight
    start.setUTCHours(0, 0, 0, 0);
    end.setUTCHours(0, 0, 0, 0);

    // Check if the dates are valid
    if (isNaN(start.getTime()) || isNaN(end.getTime())) {
      console.error("Invalid date(s):", { startDate, endDate });
      return "Invalid date";
    }

    let years = end.getFullYear() - start.getFullYear();
    let months = end.getMonth() - start.getMonth();
    let days = end.getDate() - start.getDate();

    if (days < 0) {
      months -= 1;
      const daysInPrevMonth = new Date(
        end.getFullYear(),
        end.getMonth(),
        0
      ).getDate();
      days += daysInPrevMonth;
    }

    if (months < 0) {
      years -= 1;
      months += 12;
    }

    let result = [];
    if (years > 0) result.push(`${years}y`);
    if (months > 0) result.push(`${months}m`);
    if (days > 0) result.push(`${days}d`);

    const finalResult = result.join(" ").trim() || "0d";

    return finalResult;
  };

  const getLengthOfStay = (todaysDate, dischargeDate) => {
    const startDate = new Date(todaysDate);
    const endDate = dischargeDate ? new Date(dischargeDate) : new Date();
    return calculateDuration(startDate, endDate);
  };

  const calculateLengthOfSobriety = (sobrietyDate) => {
    if (!sobrietyDate && sobrietyDate !== "") return null;

    const now = new Date();
    const startDate = new Date(sobrietyDate);
    return calculateDuration(startDate, now);
  };

  const closeDischargeModal = () => {
    setDischargeModalOpen(false);
    setCurrentResidentId(null);
  };

  const handleDischargeSubmit = async (e) => {
    e.preventDefault();
    try {
      // Get form data
      const formData = {
        dischargeStatus: e.target.dischargeStatus.value,
        sobrietyStatus: e.target.sobrietyStatus.value,
        returnability: e.target.returnability.value,
        dischargeDate: e.target.dischargeDate.value,
        dischargeNotes: e.target.dischargeNotes.value,
        currentResident: false, // Mark as discharged
      };

      // Reference to the original resident document
      const residentRef = doc(
        db,
        `organizations/${organizationId}/residents`,
        currentResidentId
      );

      // Get the complete resident data
      const residentSnapshot = await getDoc(residentRef);
      if (!residentSnapshot.exists()) {
        console.error(
          `Resident document with ID ${currentResidentId} does not exist.`
        );
        return;
      }

      // Merge the existing resident data with the discharge form data
      const residentData = residentSnapshot.data();
      const completeAlumniData = {
        ...residentData,
        ...formData,
        dischargedAt: new Date().toISOString(), // Add timestamp of when they were moved to alumni
      };

      // Create a reference to the new alumni document (using the same ID for consistency)
      const alumniRef = doc(
        db,
        `organizations/${organizationId}/alumni`,
        currentResidentId
      );

      // Execute operations in sequence
      // 1. Add to alumni collection
      await setDoc(alumniRef, completeAlumniData);

      // 2. Delete the resident document instead of updating it
      await deleteDoc(
        doc(db, `organizations/${organizationId}/residents`, currentResidentId)
      );

      // NEW CODE: Remove the resident from users collection if they exist there
      try {
        // Extract first and last name from the resident data
        const { firstName, lastName } = residentData;

        // Query the users collection to find users with matching name
        const usersRef = collection(
          db,
          `organizations/${organizationId}/users`
        );
        const q = query(
          usersRef,
          where("firstName", "==", firstName),
          where("lastName", "==", lastName),
          where("role", "==", "Resident") // Add this to ensure we only match residents
        );

        const userSnapshot = await getDocs(q);

        // If matching user found, delete them
        if (!userSnapshot.empty) {
          const userDoc = userSnapshot.docs[0];
          await deleteDoc(userDoc.ref);
          console.log(
            `Removed user ${firstName} ${lastName} from users collection`
          );
        }
      } catch (userDeleteError) {
        console.error(
          "Error removing user from users collection:",
          userDeleteError
        );
        // Don't throw here - we still want to complete the discharge process
        // even if the user deletion fails
      }

      console.log(
        `Successfully moved resident ${currentResidentId} to alumni collection and removed from residents`
      );
      closeDischargeModal();
    } catch (err) {
      console.error("Error discharging resident:", err);
      alert("There was an error processing the discharge. Please try again.");
    }
  };

  if (isLoading)
    return (
      <div className="flex items-center justify-center h-[80vh]">
        <Spinner />
      </div>
    );
  if (!user) {
    navigate("/login");
    return null;
  }

  const filteredResidents = residents
    .sort((a, b) => a.firstName.localeCompare(b.firstName))
    .filter((r) => r.currentResident)
    .filter((r) => houseFilter === "All" || r.house === houseFilter)
    .filter(
      (r) =>
        !searchInput ||
        r.firstName.toLowerCase().includes(searchInput.toLowerCase()) ||
        r.lastName.toLowerCase().includes(searchInput.toLowerCase())
    );

  const handleDeleteClick = (id) => {
    setCurrentResidentId(id);
    setDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    setCurrentResidentId(null);
    setDeleteModalOpen(false);
  };

  return (
    <>
      <Helmet>
        <title>&bull; Residents</title>
      </Helmet>

      <div className="min-h-screen bg-[#18181B] bg-gradient-to-br from-zinc-900 via-zinc-900/95 to-zinc-950 p-6 relative overflow-hidden">
        {/* Ambient background effects */}
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(24,24,27,0.3),rgba(0,0,0,0))]" />
        <div className="absolute inset-0">
          <div className="absolute top-0 -left-4 w-96 h-96 bg-cyan-500/10 rounded-full mix-blend-screen filter blur-3xl animate-pulse" />
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-blue-500/10 rounded-full mix-blend-screen filter blur-3xl animate-pulse delay-700" />
        </div>

        <div className="max-w-7xl mx-auto space-y-8 relative">
          {/* Header */}
          <div className="flex items-center gap-6 mb-12">
            <div className="flex gap-3">
              <div className="h-16 w-1 bg-gradient-to-b from-cyan-500 to-cyan-600 rounded-full animate-pulse" />
              <div className="h-16 w-1 bg-gradient-to-b from-cyan-500 to-cyan-600 rounded-full animate-pulse delay-100 opacity-70" />
              <div className="h-16 w-1 bg-gradient-to-b from-cyan-500 to-cyan-600 rounded-full animate-pulse delay-200 opacity-40" />
            </div>
            <div>
              <h1 className="text-4xl font-bold text-white tracking-tight">
                Resident Management
              </h1>
            </div>
          </div>

          {/* Search and Filter Section */}
          <div className="relative bg-slate-900/50 backdrop-blur-xl rounded-xl overflow-hidden p-4 mb-6">
            <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
              <div className="relative flex-1 max-w-md">
                <Search
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400"
                  size={20}
                />
                <input
                  type="text"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  className="flex-1 w-full pl-10 pr-3 py-2 bg-slate-800/50 text-white rounded-xl border border-slate-700 focus:border-blue-500 transition-colors"
                  placeholder="Search residents..."
                />
              </div>

              <div className="flex gap-4 items-center">
                <select
                  value={houseFilter}
                  onChange={(e) => setHouseFilter(e.target.value)}
                  className="px-4 py-2 pr-8 bg-slate-800/50 text-white rounded-xl border border-slate-700 focus:border-blue-500 transition-colors"
                >
                  <option value="All">All Houses</option>
                  {Object.keys(organizationData?.housesData || {}).map(
                    (house) => (
                      <option key={house} value={house}>
                        {house}
                      </option>
                    )
                  )}
                </select>
              </div>
            </div>
          </div>

          {/* Residents Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-4">
            {filteredResidents.map((resident) => {
              const recoveryTime = calculateLengthOfSobriety(
                resident.sobrietyDate
              );
              const timeInHouse = getLengthOfStay(resident.intakeDate);

              return (
                <div
                  key={resident.id}
                  style={getHouseColors(resident.house).style}
                  className="group relative backdrop-blur-xl rounded-lg overflow-hidden hover:shadow-2xl border"
                >
                  <div className="p-4 flex flex-col gap-3">
                    {/* Header with name and house icon */}
                    <div className="flex items-center justify-between mb-1">
                      <h3 className="text-lg font-semibold text-white truncate max-w-[70%]">
                        {resident.fullName}
                      </h3>
                      {/* Replace house data block with HouseIcon */}
                      <HouseIcon resident={resident} />
                    </div>

                    {/* Stats container */}
                    <div className="grid grid-cols-2 gap-2">
                      <div className="flex items-center gap-2 bg-gradient-to-br from-slate-800/50 to-slate-900/50 rounded-lg p-2 shadow-lg border border-slate-700">
                        <HeartPulse
                          size={16}
                          className="text-red-400 shrink-0"
                        />
                        <span className="text-slate-300 text-sm font-medium truncate">
                          {recoveryTime || "0d"}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 bg-gradient-to-br from-slate-800/50 to-slate-900/50 rounded-lg p-2 shadow-lg border border-slate-700">
                        <Home size={16} className="text-blue-400 shrink-0" />
                        <span className="text-slate-300 text-sm font-medium truncate">
                          {timeInHouse || "0d"}
                        </span>
                      </div>
                    </div>

                    {/* Action buttons */}
                    <div className="flex gap-2 mt-1">
                      {canViewProfile && (
                        <a
                          href={`/profile/${resident.id}`}
                          className="flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 bg-blue-500/30 hover:bg-blue-500/60 text-blue-300 hover:text-blue-200 rounded-lg transition-colors text-sm shadow-sm"
                        >
                          <Info size={16} className="shrink-0" />
                          <span>Profile</span>
                        </a>
                      )}
                      {canDischargeResidents && (
                        <button
                          onClick={() => openDischargeModal(resident.id)}
                          className="flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 bg-red-500/30 hover:bg-red-500/60 text-red-400 hover:text-red-300 rounded-lg transition-colors text-sm"
                        >
                          <DoorOpen size={16} className="shrink-0" />
                          <span>Discharge</span>
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
      {poReminderOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm z-50 flex justify-center items-center">
          <div className="bg-slate-900/90 border border-slate-700 rounded-xl shadow-lg w-full max-w-lg">
            <div className="px-6 py-4 border-b border-slate-700">
              <h3 className="text-xl font-semibold text-white">
                PO Notification Reminder
              </h3>
            </div>
            <div className="p-6">
              <p className="text-slate-300 text-center">
                Don't forget to reach out to {currentPOInfo.poFirstName}{" "}
                {currentPOInfo.poLastName} about the discharge.
              </p>
              <div className="flex justify-end gap-4 mt-6">
                <button
                  onClick={cancelPoReminder}
                  className="px-4 py-2 text-sm font-medium text-slate-300 hover:text-white bg-slate-800 rounded-lg border border-slate-700 hover:bg-slate-700 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={closePoReminder}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors"
                >
                  Acknowledge & Continue
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {dischargeModalOpen && canDischargeResidents && (
        <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm z-50 flex justify-center items-center">
          <div className="bg-slate-900/90 border border-slate-700 rounded-xl shadow-lg w-full max-w-lg">
            <div className="px-6 py-4 border-b border-slate-700">
              <h3 className="text-xl font-semibold text-white">
                Discharge Form
              </h3>
            </div>

            <form onSubmit={handleDischargeSubmit} className="p-6">
              <div className="space-y-4">
                {/* Discharge Status */}
                <div className="relative">
                  <label
                    htmlFor="dischargeStatus"
                    className="block text-sm font-medium text-slate-300 mb-1"
                  >
                    Discharge Status
                  </label>
                  <select
                    id="dischargeStatus"
                    className="w-full px-3 py-2 bg-slate-800/50 border border-slate-700 rounded-lg text-white focus:outline-none focus:border-red-500 focus:ring-1 focus:ring-red-500 transition-colors"
                    required
                  >
                    <option value="Healthy Transition">
                      Healthy Transition
                    </option>
                    <option value="Removed">Removed</option>
                    <option value="Against Advice">Against Advice</option>
                  </select>
                </div>

                {/* Sobriety Status */}
                <div className="relative">
                  <label
                    htmlFor="sobrietyStatus"
                    className="block text-sm font-medium text-slate-300 mb-1"
                  >
                    Sobriety Status
                  </label>
                  <select
                    id="sobrietyStatus"
                    className="w-full px-3 py-2 bg-slate-800/50 border border-slate-700 rounded-lg text-white focus:outline-none focus:border-red-500 focus:ring-1 focus:ring-red-500 transition-colors"
                    required
                  >
                    <option value="Sober">Sober</option>
                    <option value="Not Sober">Not Sober</option>
                  </select>
                </div>

                {/* Returnability */}
                <div className="relative">
                  <label
                    htmlFor="returnability"
                    className="block text-sm font-medium text-slate-300 mb-1"
                  >
                    Return Eligibility
                  </label>
                  <select
                    id="returnability"
                    className="w-full px-3 py-2 bg-slate-800/50 border border-slate-700 rounded-lg text-white focus:outline-none focus:border-red-500 focus:ring-1 focus:ring-red-500 transition-colors"
                    required
                  >
                    <option value="Yes">Yes</option>
                    <option value="No">No</option>
                  </select>
                </div>

                {/* Discharge Date */}
                <div className="relative">
                  <label
                    htmlFor="dischargeDate"
                    className="block text-sm font-medium text-slate-300 mb-1"
                  >
                    Discharge Date
                  </label>
                  <input
                    type="date"
                    id="dischargeDate"
                    className="w-full px-3 py-2 bg-slate-800/50 border border-slate-700 rounded-lg text-white focus:outline-none focus:border-red-500 focus:ring-1 focus:ring-red-500 transition-colors"
                    required
                  />
                </div>

                {/* Discharge Notes */}
                <div className="relative">
                  <label
                    htmlFor="dischargeNotes"
                    className="block text-sm font-medium text-slate-300 mb-1"
                  >
                    Discharge Notes
                  </label>
                  <textarea
                    id="dischargeNotes"
                    className="w-full px-3 py-2 bg-slate-800/50 border border-slate-700 rounded-lg text-white focus:outline-none focus:border-red-500 focus:ring-1 focus:ring-red-500 transition-colors min-h-[100px] resize-none"
                    required
                  />
                </div>
              </div>

              <div className="flex justify-end gap-4 mt-6">
                <button
                  type="button"
                  onClick={closeDischargeModal}
                  className="px-4 py-2 text-sm font-medium text-slate-300 hover:text-white bg-slate-800/50 hover:bg-slate-800/70 rounded-lg border border-slate-700 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 text-sm font-medium text-white bg-red-500/80 hover:bg-red-500 rounded-lg transition-colors"
                >
                  Discharge
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {deleteModalOpen && (
        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="min-h-screen px-4 text-center">
            <div className="fixed inset-0 bg-black opacity-30" />
            <div className="inline-block w-full max-w-md p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-gray-600 shadow-xl rounded-2xl">
              <div className="text-3xl font-mono font-medium leading-6 text-red-700 text-center">
                Delete Resident
              </div>
              <p className="mt-4">
                This will delete all resident information from the database. Are
                you sure?
              </p>
              <div className="mt-4 flex justify-end">
                <button
                  type="button"
                  className="mr-2 bg-green-600 hover:bg-green-800 font-bold py-2 px-4 rounded shadow-md"
                  onClick={() => handleDeleteClick(resident.id)}
                >
                  Delete
                </button>
                <button
                  type="button"
                  className="bg-red-700 hover:bg-red-800 font-bold py-2 px-4 rounded shadow-md mr-2"
                  onClick={closeDeleteModal}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Residents;
